"use client";

import classNames from "classnames";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Fieldset } from "../fieldset";
import styles from "./FileInput.module.scss";
import type { FileInputTypes } from "./FileInput.types";

const DEFAULT_EMPTY = "Przeciągnij lub wybierz plik";

export const FileInput = ({
  id,
  name,
  label,
  required = false,
  emptyLabel = DEFAULT_EMPTY,
  value,
  defaultValue = null,
  onChange,
  disabled = false,
  error,
  variant = "secondary",
  fullWidth = false,
  accept,
  multiple = false,
  className = "",
  startContent = null,
}: FileInputTypes) => {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [internalFile, setInternalFile] = useState<File | null>(defaultValue);
  const [isDragging, setIsDragging] = useState(false);

  const isControlled = value !== undefined;
  const file = isControlled ? value : internalFile;

  const setFile = useCallback(
    (next: File | null) => {
      if (!isControlled) {
        setInternalFile(next);
      }
      onChange?.(next);
    },
    [isControlled, onChange]
  );

  useEffect(() => {
    if (!isControlled || value !== null) return;
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, [isControlled, value]);

  const applyFile = useCallback(
    (f: File | null) => {
      setFile(f);
    },
    [setFile]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files;
    const f = list?.length ? list[0] : null;
    applyFile(f);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (disabled) return;
    dragDepth.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    if (disabled) return;
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    if (disabled) return;
    const files = e.dataTransfer.files;
    if (!files?.length) return;
    applyFile(files[0]);
    if (inputRef.current) {
      try {
        const dt = new DataTransfer();
        dt.items.add(files[0]);
        inputRef.current.files = dt.files;
      } catch {
        /* ignore if DataTransfer not available */
      }
    }
  };

  const displayText = file ? file.name : emptyLabel;
  const isPlaceholder = !file;

  return (
    <Fieldset error={error}>
      <div
        className={classNames(
          styles["comp"],
          fullWidth && styles["comp--full-width"],
          className
        )}
      >
        {label ? (
          <div className={styles["comp__label"]}>
            <span id={`${controlId}-label`}>{label}</span>
            {required ? <span>*</span> : null}
          </div>
        ) : null}

        <div
          className={classNames(
            styles["comp__zone"],
            styles[`comp__zone--variant-${variant}`],
            isDragging && styles["comp__zone--drag"],
            disabled && styles["comp__zone--disabled"],
            error && styles["comp__zone--error"]
          )}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
        >
          <input
            ref={inputRef}
            id={controlId}
            name={name}
            type="file"
            className={styles["comp__native"]}
            disabled={disabled}
            accept={accept}
            multiple={multiple}
            aria-labelledby={label ? `${controlId}-label` : undefined}
            aria-invalid={Boolean(error)}
            onChange={handleInputChange}
            onDrop={handleDrop}
          />
          {startContent ? <span className={styles["comp__start"]}>{startContent}</span> : null}
          <span
            className={classNames(
              styles["comp__text"],
              isPlaceholder ? styles["comp__text--placeholder"] : styles["comp__text--file"]
            )}
            title={displayText}
          >
            {displayText}
          </span>
        </div>
      </div>
    </Fieldset>
  );
};
